import Series from '../../entities/Series.js';
import SeriesConverterBase from '../SeriesConverterBase.js';

/**
 * https://en.wikipedia.org/wiki/Moving_average
 */
class MovingAverageSeriesConverter extends SeriesConverterBase {
    /**
     * Converts series into moving average series
     * @param {Integer} subsetSize - size of the subset which will be used to calculate moving average, cannot be less than 1
     * @param {Array} dimensions - array of series dimensions which will be converted using moving average, dimension cannot be less than 1
     * @param {Number|undefined} includeNaNs - default true, when set to true NaN values will be included in subsets for average calculation, otherwise it will be ignored in subset
     * @param {Number|undefined} averagePointOffset - default center value, allows to define offset of average for subset, cannot be less than 0 or greater than subsetSize
     */
    constructor(subsetSize, dimensions, includeNaNs, averagePointOffset) {
        super();

        if (typeof(subsetSize) !== 'number' || !Number.isInteger(subsetSize)) {
            throw new TypeError('subsetSize must be an integer');
        }

        if (subsetSize < 1) {
            throw new TypeError('subsetSize must be an greater than 0');
        }

        if (!(dimensions instanceof Array)) {
            throw new TypeError('dimensions must be an array');
        }

        if (dimensions.some(dimension => typeof(dimension) !== 'number' || !Number.isInteger(dimension) || dimension < 1)) {
            throw new TypeError('dimensions must contain array of integers greater than 0');
        }

        if (typeof(includeNaNs) === 'undefined') {
            this.includeNaNs = true;
        }
        else if (typeof(includeNaNs) !== 'boolean') {
            throw new TypeError('includeNaNs must be an boolean value or undefined');
        }
        else {
            this.includeNaNs = includeNaNs;
        }

        if (typeof(averagePointOffset) === 'undefined') {
            this.averagePointOffset = (subsetSize - (subsetSize % 2)) / 2;
        }
        else if (typeof(averagePointOffset) !== 'number' || !Number.isInteger(averagePointOffset)) {
            throw new TypeError('averagePointOffset must be an integer or undefined');
        }
        else if (averagePointOffset < 0 || averagePointOffset > subsetSize) {
            throw new Error('averagePointOffset must be greater than zero and cannot be greater than subsetSize');
        }
        else {
            this.averagePointOffset = averagePointOffset;
        }

        this.subsetSize = subsetSize;
        this.dimensions = dimensions;
    }

    /**
     * Private method, which needs to be implemented in inheriting class
     * @param {Series} series - data series which will be converted
     */
    _convert(series) {
        const converted = new Series(`${series.name} - AVG(${this.subsetSize})`, series.dimensions);

        for (let i = 0; i < series.length; ) {
            const subset = [];
            const sums = [].concat(this.dimensions).fill(0, 0, this.dimensions.length);
            const sources = [];
            let numbers = 0;
            
            subnets:
            while (numbers < this.subsetSize && i < series.length) {
                for (let d = 0; d < this.dimensions.length; d++) {
                    const dimension = this.dimensions[d];
                    const subsetValue = series.getValue(i, dimension);
                    if (Number.isNaN(subsetValue) || subsetValue === null) {
                        if (!this.includeNaNs) {
                            i++;
                            continue subnets;
                        }
                    }
                    else {
                        sums[d] += subsetValue;
                    }
                }

                numbers++;

                subset.push(series.getValue(i));
                sources.push(series.getSource(i));
                i++;
            }
            
            if (numbers === this.subsetSize) {
                if (series.dimensions === 1) {
                    // we need a little different approach here
                    converted.add(sources, sums.shift() / numbers);
                }
                else {
                    // now we need to rebuild this stuff...
                    const newValues = [];
                    for (let d = 0; d < series.dimensions; d++) {
                        const dimensionIndex = this.dimensions.indexOf(d+1);
                        if (dimensionIndex > -1) {
                            // we should replace it with average
                            newValues[d] = sums[dimensionIndex] / numbers;
                        }
                        else {
                            // otherwise just pick value based on index
                            newValues[d] = subset[this.averagePointOffset][d];
                        }
                    }

                    converted.add(sources, ...newValues);
                }
            }
        }

        return converted;
    }
}

export default MovingAverageSeriesConverter;